import { motion } from 'framer-motion';
import { Upload, Cpu, Rocket } from 'lucide-react';

const steps = [
  {
    icon: Upload,
    step: '01',
    title: 'Upload Your Data',
    desc: 'Feed Pulse your knowledge base — PDFs, websites, support history, CSVs.',
    tags: ['Docs', 'Websites', 'Support history'],
    gradA: '#479EC3', gradB: '#22D3EE',
  },
  {
    icon: Cpu,
    step: '02',
    title: 'Train Your AI',
    desc: 'Fine-tune tone, add custom workflows, and configure how your AI handles every scenario.',
    tags: ['Fine-tune tone', 'Customize behavior'],
    gradA: '#22D3EE', gradB: '#6366f1',
  },
  {
    icon: Rocket,
    step: '03',
    title: 'Deploy Instantly',
    desc: 'Go live across all your channels in one click. No engineering required.',
    tags: ['Website', 'WhatsApp'],
    gradA: '#6366f1', gradB: '#479EC3',
  },
];

export default function HowItWorks() {
  return (
    <section id="how-it-works" style={{ padding: '100px 24px', position: 'relative', overflow: 'hidden' }}>
      {/* Left glow */}
      <div style={{ position: 'absolute', top: '40%', left: 0, width: 350, height: 350, background: 'rgba(71,158,195,0.07)', borderRadius: '50%', filter: 'blur(100px)', pointerEvents: 'none' }} />

      <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          style={{ textAlign: 'center', marginBottom: 72 }}>
          <p style={{ fontSize: 13, color: '#479EC3', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>How It Works</p>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.02em' }}>
            From data to deployed AI<br />in three steps
          </h2>
        </motion.div>

        <div className="hiw-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, position: 'relative', alignItems: 'stretch' }}>
          {/* Connector line */}
          <div className="hiw-connector" style={{
            position: 'absolute', top: 48, left: '17%', right: '17%', height: 1,
            background: 'linear-gradient(to right, #479EC3, #22D3EE, #6366f1)',
            opacity: 0.25, zIndex: 0,
          }} />

          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div key={s.step}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Step number + icon */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
                  <motion.div
                    whileHover={{ scale: 1.08 }}
                    style={{
                      width: 72, height: 72, borderRadius: '50%',
                      background: `linear-gradient(135deg,${s.gradA},${s.gradB})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: `0 0 32px ${s.gradA}55`,
                      marginBottom: 12,
                    }}>
                    <Icon size={30} color="#fff" />
                  </motion.div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '0.12em' }}>STEP {s.step}</span>
                </div>

                {/* Card */}
                <div style={{
                  background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(16px)',
                  border: '1px solid rgba(30,41,59,0.6)', borderRadius: 14, padding: 28, textAlign: 'center',
                  flex: 1, display: 'flex', flexDirection: 'column',
                }}>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', marginBottom: 10 }}>{s.title}</h3>
                  <div style={{ minHeight: '5rem', marginBottom: 20 }}>
                    <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.6, margin: 0 }}>{s.desc}</p>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                    {s.tags.map(tag => (
                      <span key={tag} style={{
                        fontSize: 12, fontWeight: 500, padding: '4px 10px', borderRadius: 999,
                        background: `linear-gradient(135deg,${s.gradA}22,${s.gradB}22)`,
                        border: `1px solid ${s.gradA}44`,
                        color: s.gradA,
                      }}>{tag}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .hiw-grid { grid-template-columns: 1fr !important; }
          .hiw-connector { display: none !important; }
        }
      `}</style>
    </section>
  );
}
